import React, { useState, useEffect, useRef } from 'react';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import { useColorMode } from '../context/ColorMode';
import { Link, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { initializeRemoteConfig, getRemoteConfigValue } from '../services/remoteConfig';
import { getUserRole } from '../services/userService';
import {
    AppBar,
    Tooltip,
    Toolbar,
    Typography,
    Box,
    List,
    Collapse,
    Stack,
    ListItem,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    useMediaQuery,
    useTheme,
    IconButton,
    Drawer,
    Button,
    Divider,
    Chip,
    Menu,
    MenuItem,
    Fade,
    Skeleton,
    Snackbar,
    Alert,
} from '@mui/material';
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import DashboardIcon from '@mui/icons-material/Dashboard';
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import InsightsIcon from '@mui/icons-material/Insights';
import { GOOGLE_UNIFIED_PATH } from '../utils/googleIntegrationPath';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import {
    Menu as MenuIcon,
    Logout as LogoutIcon,
    AccountCircle as AccountCircleIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Article as ArticleIcon,
    Folder as FolderIcon,
    GroupWork as GroupWorkIcon,
    Key as KeyIcon,
    Language as LanguageIcon,
    Brush as BrushIcon,
    Description as DescriptionIcon,
    History as HistoryIcon,
    Work as WorkIcon,
    KeyboardArrowDownRounded,
    KeyboardArrowUpRounded,
    NorthEastRounded
} from '@mui/icons-material';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import getIdToken from '../components/getIdToken';
import readConnectionId, { clearConnectionId } from '../utils/readShopifyConnectionId';
import ShopifyConnectPrompt from '../components/ShopifyConnectPrompt';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import WebIcon from '@mui/icons-material/Web';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import { auth } from '../config/firebase';
import { useCredits } from '../contexts/CreditsContext';
import { DetailSkeleton } from '../components/skeletons';
import logoWhite from '../assets/logo_white_nobottomwords.png';

const Dashboard = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const user = auth.currentUser;
    const prevPathRef = useRef(location.pathname);
    const [shopifyConnection, setShopifyConnection] = useState(() => readConnectionId());

    // State management
    const colorMode = useColorMode();
    const [selectedItem, setSelectedItem] = useState('');
    // Which sidebar items have their submenu expanded (keyed by item.key).
    const [openMenus, setOpenMenus] = useState({});
    const [openDrawer, setOpenDrawer] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownOpen = Boolean(anchorEl);
    const [menuStructure, setMenuStructure] = useState(null);
    const [accessConfig, setAccessConfig] = useState(null);
    const [userRole, setUserRole] = useState('user');
    const [loadingUserRole, setLoadingUserRole] = useState(true);
    const [remoteConfigInitialized, setRemoteConfigInitialized] = useState(false);
    const [menuLoading, setMenuLoading] = useState(true);

    // One-shot "look here" highlight on the Domains nav item — set by
    // DomainConnectDialog when it registers a domain on the user's behalf,
    // so the next time the dashboard mounts the user gets a soft cue that a
    // new entry just landed in Domains. Cleared after the 6s pulse runs.
    const [highlightDomains, setHighlightDomains] = useState(false);
    useEffect(() => {
        try {
            if (localStorage.getItem('xeo_highlight_domains_tab') === '1') {
                localStorage.removeItem('xeo_highlight_domains_tab');
                setHighlightDomains(true);
                const t = setTimeout(() => setHighlightDomains(false), 6000);
                return () => clearTimeout(t);
            }
        } catch (_) { /* localStorage disabled — skip the cue */ }
        return undefined;
    }, []);
    const { planData, refreshCredits, loading, planLoaded, plansEnabled, customersEnabled, creditsError } = useCredits();
    // The header's balance could not be read: it shows "—", and the account menu
    // offers a retry instead of a 0 the user might act on.
    const [retryingBalance, setRetryingBalance] = useState(false);
    const retryBalance = async () => {
        setRetryingBalance(true);
        try { await refreshCredits(); } finally { setRetryingBalance(false); }
    };

    /* A pending Shopify connection is ASKED about now (ShopifyConnectPrompt),
       not attached silently on mount — whoever signed in next on this browser
       used to get the shop attached to their account. */
    const [shopifyNote, setShopifyNote] = useState(null);
    // Remounts the prompt so it looks again: it reads the stored id once, on mount.
    const [shopifyPromptKey, setShopifyPromptKey] = useState(0);
    // An id this account minted from Add/Edit Domain is already saved against
    // it: never offered for attaching, even while Add Domain keeps it stored.
    const ownDomainFlow = (c) => !!c && (c.source === 'add' || c.source === 'edit')
        && !!c.userId && c.userId === auth.currentUser?.uid;

    useEffect(() => {
        const channel = new BroadcastChannel('shopify_connect');
        channel.onmessage = (e) => {
            const { connectionId, shop, source, userId, createdAt } = e.data || {};
            if (!connectionId) return;
            /*
             * NEVER ATTACHED FROM HERE. This attached the shop to whoever was
             * signed in in ANY open tab the moment a callback finished anywhere in
             * the browser — no question, no check that this account started it,
             * no word either way.
             *
             * Started from Add/Edit Domain by this account: the callback already
             * saved the shop against it (the OAuth state carries the uid), so
             * there is nothing to attach and nothing to ask (see ownDomainFlow).
             * An Edit id is dropped. An Add id stays for Add Domain: it reads it
             * on mount to prefill the shop after a reload, and clears it once the
             * domain is created. Plans ignores both (billsViaShopify). Anything
             * else is kept and ASKED about — ShopifyConnectPrompt checks the id
             * was minted for this account.
             */
            if ((source === 'add' || source === 'edit') && userId && userId === auth.currentUser?.uid) {
                if (source === 'edit') clearConnectionId();
                setShopifyConnection(readConnectionId());
                return;
            }
            const payload = { value: connectionId, shop, createdAt: createdAt || Date.now(), ...(source && { source }), ...(userId && { userId }) };
            localStorage.setItem('connectionId', JSON.stringify(payload));
            setShopifyConnection(readConnectionId());
            setShopifyPromptKey((k) => k + 1);
        };
        return () => channel.close();
    }, []);

    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        if (urlParams.get('refreshCredits') === 'true') {
            const newUrl = location.pathname;
            navigate(newUrl, { replace: true });
            setTimeout(() => {
                refreshCredits();
            }, 1000);
        }
    }, [location.search, navigate, refreshCredits]);

    const [collapsed, setCollapsed] = useState(() => {
        const storedValue = localStorage.getItem('collapsed');
        return storedValue === 'true' ? true : false;
    });
    const [isTransitioning, setIsTransitioning] = useState(false);
    const isGlobalLoading = false;
    // The desktop rail's scroller. State rather than a ref so the effects that
    // watch it re-run when it mounts: after the credits load, and on coming back
    // from a phone width.
    const [railScroller, setRailScroller] = useState(null);
    const railRevealedRef = useRef(false);
    const [railMoreBelow, setRailMoreBelow] = useState(false);

    // Update your fetchUserRole function to be more defensive:
    useEffect(() => {
        const fetchUserRole = async () => {
            if (user) {
                try {
                    const role = await getUserRole(user.uid);
                    setUserRole(role);
                } catch (error) {
                    console.error('Error fetching user role:', error);
                    setUserRole('user');
                } finally {
                    setLoadingUserRole(false);
                }
            } else {
                setUserRole('user');
                setLoadingUserRole(false);
            }
        };
        fetchUserRole();
    }, [user]);

    // Initialize remote config and fetch values
    useEffect(() => {
        const initConfig = async () => {
            if (loadingUserRole) return;
            const initialized = await initializeRemoteConfig();
            setRemoteConfigInitialized(initialized);

            if (initialized) {
                // Get menu structure from remote config
                const menuConfig = getRemoteConfigValue('dashboard_menu_structure');
                if (menuConfig) {
                    setMenuStructure(menuConfig);
                }

                // Get access config from remote config
                const accessConfig = getRemoteConfigValue('dashboard_access_config');
                if (accessConfig) {
                    setAccessConfig(accessConfig);
                }
            }
            setMenuLoading(false);
        };
        initConfig();
    }, [loadingUserRole, userRole]);

    // Check if user has access to a specific menu item
    const hasAccess = (itemKey) => {
        if (!remoteConfigInitialized || !accessConfig || loadingUserRole) {
            return true;
        }

        // Check if the item exists in the access config
        if (!accessConfig[itemKey]) {
            return true;
        }

        // Check if the user role exists in the item's access config
        if (accessConfig[itemKey][userRole] === undefined) {
            return true;
        }

        const hasAccess = accessConfig[itemKey][userRole] === true;
        return hasAccess;
    };

    // Build menu structure from remote config
    const buildMenuSections = () => {
        if (!menuStructure || loadingUserRole || true) { // Force fallback menu for testing
            // Fallback to default menu 
            const sections = [
                {
                    title: 'Dashboard',
                    items: [
                        { key: 'statistics', label: 'Statistics', icon: <DashboardIcon sx={{ color: 'var(--accent)' }} />, enabled: hasAccess('domains') },
                    ].filter(item => item.enabled)
                },
                {
                    title: 'XSite',
                    items: [
                        { key: 'xsite-builder', label: 'Website Builder', icon: <BuildCircleIcon sx={{ color: 'var(--accent)' }} />, enabled: hasAccess('xsite-builder') },
                        { key: 'past-sites', label: 'Past Sites', icon: <WebIcon sx={{ color: 'var(--accent)' }} />, enabled: hasAccess('past-sites') },
                        { key: 'past-site-jobs', label: 'Past Site Jobs', icon: <WorkHistoryIcon sx={{ color: 'var(--accent)' }} />, enabled: hasAccess('past-site-jobs') },
                        { key: 'customers', label: 'Customers', icon: <PeopleAltOutlinedIcon sx={{ color: 'var(--accent)' }} />, enabled: customersEnabled && hasAccess('customers') }
                    ].filter(item => item.enabled)
                },
                {
                    title: 'XWatch',
                    items: [
                        { key: 'domains', label: 'Domains', icon: <LanguageIcon sx={{ color: 'var(--accent)' }} />, enabled: hasAccess('domains') },
                        { key: 'uptime', label: 'Uptime', icon: <MonitorHeartOutlinedIcon sx={{ color: 'var(--accent)' }} />, enabled: true },
                        {
                            key: 'analytics', label: 'Traffic Analytics', icon: <InsightsIcon sx={{ color: 'var(--accent)' }} />, enabled: true,
                            children: [
                                { view: 'traffic', label: 'Overview' },
                                { view: 'search', label: 'Search Console' },
                                { view: 'compare', label: 'Compare Sites' },
                            ]
                        },
                        {
                            key: 'seo', label: 'SEO Suite', icon: <TravelExploreIcon sx={{ color: 'var(--accent)' }} />, enabled: hasAccess('seo'),
                            children: [
                                { view: 'competitors', label: 'Competitors' },
                                { view: 'backlinks', label: 'Backlinks' },
                                { view: 'keywords', label: 'Keywords Ranking' },
                                { view: 'content', label: 'Content Ideas' },
                                { view: 'actions', label: 'Action Plan' },
                                { view: 'results', label: 'Content Analytics' },
                            ]
                        },
                    ].filter(item => item.enabled)
                },
                {
                    title: 'XWrite',
                    items: [
                        { key: 'article-writer', label: 'AI Writer', icon: <HistoryEduIcon sx={{ color: 'var(--accent)' }} />, enabled: hasAccess('article-writer') },
                        { key: 'past-articles', label: 'Past Articles', icon: <HistoryIcon sx={{ color: 'var(--accent)' }} />, enabled: hasAccess('past-articles') },
                        { key: 'past-writing-jobs', label: 'Past Writing Jobs', icon: <WorkIcon sx={{ color: 'var(--accent)' }} />, enabled: hasAccess('past-writing-jobs') },
                        // Shortcut into the SEO-suite "Content results" tab (canonical home).
                        // It's the "did my articles work?" question, so surface it where you write —
                        // but keep ONE source of truth by deep-linking rather than duplicating the tab.
                        { key: 'content-results', label: 'Content Analytics', icon: <AutoGraphIcon sx={{ color: 'var(--accent)' }} />, enabled: hasAccess('seo'), path: '/dashboard/seo?view=results' },
                    ].filter(item => item.enabled)
                },
                {
                    title: 'Project Management',
                    items: [
                        { key: 'projects', label: 'Projects', icon: <FolderIcon sx={{ color: 'var(--accent)' }} />, enabled: hasAccess('projects') },
                        { key: 'keywordsgroup', label: 'Keyword Groups', icon: <GroupWorkIcon sx={{ color: 'var(--accent)' }} />, enabled: hasAccess('keywordsgroup') },
                        { key: 'keywords', label: 'Keywords', icon: <KeyIcon sx={{ color: 'var(--accent)' }} />, enabled: hasAccess('keywords') },
                        { key: 'templates', label: 'Templates', icon: <BrushIcon sx={{ color: 'var(--accent)' }} />, enabled: hasAccess('templates') },
                    ].filter(item => item.enabled)
                },
            ];

            // Filter out sections with no items
            return sections.filter(section => section.items.length > 0);
        }
    };

    const mainMenuSections = React.useMemo(() => {
        if (menuLoading || loadingUserRole) {
            return [];
        }
        return buildMenuSections();
    // customersEnabled: this now runs during the credits load too (the early
    // returns moved below the hooks), so it must re-run when that flag lands.
    }, [menuStructure, accessConfig, userRole, menuLoading, loadingUserRole, customersEnabled]);

    // Initialize state based on current URL path
    useEffect(() => {
        const path = location.pathname.split('/');
        const currentPath = path.slice(2).join('/');
        const mainItem = currentPath.split('/')[0];
        setSelectedItem(mainItem);
        if (!mainItem) return;   // /dashboard itself selects nothing
        localStorage.setItem('selectedItem', mainItem);
        // Auto-expand the active item's submenu so you land with its options open.
        setOpenMenus((prev) => (prev[mainItem] ? prev : { ...prev, [mainItem]: true }));
    }, [location]);

    /*
     * Keep the active item in view in the desktop rail. At 900px tall the open
     * rail's list runs on past the toggle row, so /dashboard/projects landed with
     * Projects cut in half and Keywords and Templates out of sight. 'nearest', with
     * the 8px of room the list's own padding leaves at its ends. Scrolls the rail's
     * box only — scrollIntoView would move every scrollable ancestor along with it.
     * Instant the first time a rail shows its menu, smooth after that.
     *
     * After a collapse toggle it waits for the rail's 0.3s width transition: at the
     * starting 80px an open submenu's labels wrap a character per line (the SEO
     * submenu measures 1592px instead of 208), so a reveal measured mid-animation
     * jumped the rail hundreds of pixels the wrong way.
     */
    const railPrevCollapsedRef = useRef(collapsed);
    const railSettledAtRef = useRef(0);
    useEffect(() => {
        const scroller = railScroller;
        if (!scroller) { railRevealedRef.current = false; return undefined; }
        if (!mainMenuSections.length) return undefined;
        const still = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (railPrevCollapsedRef.current !== collapsed) {
            railPrevCollapsedRef.current = collapsed;
            railSettledAtRef.current = Date.now() + 350;
        }
        const reveal = () => {
            const smooth = railRevealedRef.current;
            railRevealedRef.current = true;
            const key = location.pathname.split('/')[2];
            const item = key && [...scroller.querySelectorAll('[data-nav-key]')].find((el) => el.dataset.navKey === key);
            if (!item) return;
            // A section's first item brings its caption along, so a reveal upward
            // never stops on half of one.
            const prev = item.previousElementSibling;
            const headTop = (prev && prev.hasAttribute('data-nav-caption') ? prev : item).getBoundingClientRect().top;
            const view = scroller.getBoundingClientRect();
            const box = item.getBoundingClientRect();
            const delta = headTop - 8 < view.top ? headTop - 8 - view.top
                : box.bottom + 8 > view.bottom ? box.bottom + 8 - view.bottom : 0;
            const top = Math.min(Math.max(0, scroller.scrollTop + delta), scroller.scrollHeight - scroller.clientHeight);
            if (Math.abs(top - scroller.scrollTop) < 1) return;
            scroller.scrollTo({ top, behavior: smooth && !still ? 'smooth' : 'auto' });
        };
        const wait = still ? 0 : railSettledAtRef.current - Date.now();
        if (wait <= 0) { reveal(); return undefined; }
        // Mid-transition (a toggle, or a route change right after one): reveal when
        // the rail's width settles, with a timer in case transitionend never comes.
        const rail = scroller.parentElement;
        let done = false;
        const run = () => { if (!done) { done = true; reveal(); } };
        const onEnd = (e) => { if (e.target === rail && e.propertyName === 'width') run(); };
        if (rail) rail.addEventListener('transitionend', onEnd);
        const timer = setTimeout(run, wait);
        return () => {
            done = true;
            if (rail) rail.removeEventListener('transitionend', onEnd);
            clearTimeout(timer);
        };
    // pathname, not location: a list page's ?page=2 is not a reason to move the rail.
    }, [railScroller, location.pathname, mainMenuSections, collapsed]);

    // Whether the rail's list runs on below the toggle row (see the row's shade).
    // Measured to its last row, not the scroll end: the list's own end padding,
    // left below once the last item is revealed, isn't more.
    useEffect(() => {
        const scroller = railScroller;
        if (!scroller) return undefined;
        const sync = () => {
            const last = scroller.firstElementChild && scroller.firstElementChild.lastElementChild;
            setRailMoreBelow(!!last && last.getBoundingClientRect().bottom - scroller.getBoundingClientRect().bottom > 1);
        };
        sync();
        scroller.addEventListener('scroll', sync, { passive: true });
        // Sizes move it too: the window, a submenu opening, the rail collapsing.
        const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(sync);
        [scroller, scroller.firstElementChild].forEach((el) => { if (ro && el) ro.observe(el); });
        return () => {
            scroller.removeEventListener('scroll', sync);
            if (ro) ro.disconnect();
        };
    }, [railScroller]);

    const handleCollapse = () => {
        setIsTransitioning(true);
        setTimeout(() => {
            setCollapsed(prev => {
                const newValue = !prev;
                localStorage.setItem('collapsed', newValue);
                return newValue;
            });
            setTimeout(() => {
                setIsTransitioning(false);
            }, 300);
        }, 10);
    };

    const handleItemClick = (item, path) => {
        // `path` items (e.g. the XWrite → Content results shortcut) are gated at render
        // via `enabled`; they deep-link to another section rather than /dashboard/<key>.
        if (!path && !hasAccess(item)) {
            console.warn('Access denied to:', item);
            return;
        }

        setSelectedItem(item);
        localStorage.setItem('aiContentOpen', 'false');
        navigate(path || `/dashboard/${item}`);

        if (isMobile) {
            setOpenDrawer(false);
        }
    };

    // Deep-link a submenu child into its page's tab (?view=...).
    const handleSubItemClick = (itemKey, view) => {
        if (!hasAccess(itemKey)) return;
        setSelectedItem(itemKey);
        setOpenMenus((prev) => ({ ...prev, [itemKey]: true }));
        navigate(`/dashboard/${itemKey}?view=${view}`);
        if (isMobile) setOpenDrawer(false);
    };

    const toggleMenu = (itemKey, e) => {
        if (e) { e.preventDefault(); e.stopPropagation(); }
        setOpenMenus((prev) => ({ ...prev, [itemKey]: !prev[itemKey] }));
    };

    // The submenu list rendered under an expandable item (shared by drawer +
    // desktop-expanded rails). Highlights the active child from the ?view= param
    // (falling back to the first child when no view is set).
    const currentView = new URLSearchParams(location.search).get('view') || '';
    const renderSubmenu = (item) => {
        if (!item.children?.length) return null;
        return (
            <Collapse in={!!openMenus[item.key]} timeout={220} unmountOnExit>
                {/* Indented list hung off a soft vertical guide rail, aligned under
                    the parent icon — reads as a clean tree of options. pr keeps the
                    selected pill off the sidebar's right edge (matches parent items). */}
                <Box sx={{ ml: 2.9, pl: 1.6, pr: 1.2, mt: 0.25, mb: 0.75, borderLeft: '1.5px solid var(--line)' }}>
                    {item.children.map((child) => {
                        const active = selectedItem === item.key &&
                            (currentView === child.view || (!currentView && child.view === item.children[0].view));
                        return (
                            <ListItemButton
                                key={child.view}
                                dense
                                component={Link}
                                to={`/dashboard/${item.key}?view=${child.view}`}
                                onClick={(e) => {
                                    if (e.ctrlKey || e.metaKey || e.button === 1) return;
                                    e.preventDefault();
                                    handleSubItemClick(item.key, child.view);
                                }}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1.1,
                                    borderRadius: '8px', py: 0.45, px: 1, mb: 0.2, minHeight: 32,
                                    transition: 'background-color .15s',
                                    '&:hover': { backgroundColor: 'var(--tint)' },
                                    ...(active && { backgroundColor: 'var(--tint-2)', '&:hover': { backgroundColor: 'var(--tint-2)' } }),
                                }}
                            >
                                <Box sx={{
                                    width: active ? 7 : 6, height: active ? 7 : 6, borderRadius: '50%', flexShrink: 0,
                                    bgcolor: active ? '#1976d2' : 'var(--line-2)',
                                    boxShadow: active ? '0 0 0 3px rgba(25,118,210,0.15)' : 'none',
                                    transition: 'all .15s',
                                }} />
                                <ListItemText primary={child.label} sx={{
                                    my: 0,
                                    '& .MuiTypography-root': {
                                        fontSize: '0.82rem', letterSpacing: '0.01em',
                                        color: active ? 'var(--accent)' : 'var(--ink-3)',
                                        fontWeight: active ? 700 : 500,
                                        transition: 'color .15s',
                                    },
                                }} />
                            </ListItemButton>
                        );
                    })}
                </Box>
            </Collapse>
        );
    };

    const handleLogout = () => {
        try {
            auth.signOut();
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const handleProfileClick = () => {
        navigate('/dashboard/profile');
    };

    // Redirect on logout...
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (!user) {
                localStorage.removeItem('selectedItem');
                localStorage.removeItem('snackbarMessage');
                localStorage.removeItem('redirectAfterLogin');
                localStorage.removeItem('redirectAfterLoginKey');
                localStorage.removeItem('connectionId');
                /* A FULL load, not navigate(): in-memory caches (the GSC
                   catalog with per-account access tokens, the Google account
                   resolver) are not keyed by user, and the next person to sign
                   in on this tab could be served the last one's. */
                window.location.assign('/');
            }
        });
        return () => unsubscribe();
    }, [navigate]);

    const toggleDropdown = (event) => {
        if (anchorEl) {
            setAnchorEl(null);
        } else {
            setAnchorEl(event.currentTarget);
        }
    }
    const handleClose = () => {
        setAnchorEl(null);
    };

    useEffect(() => {
        const prev = prevPathRef.current;
        const current = location.pathname;

        // Check if we LEFT the domains page
        if (prev.startsWith("/dashboard/domains") && !current.startsWith("/dashboard/domains")) {
            localStorage.removeItem("domainFilters");
        }

        prevPathRef.current = current;
    }, [location.pathname]);

    /*
     * AFTER EVERY HOOK, not before. These returns sat above a dozen useEffects,
     * so the hook count changed between the loading render and the loaded one —
     * React's rules-of-hooks error waiting for the first remount that did not
     * already have credits. Latent only because ProtectedRoute waits for auth.
     */
    if (loading) {
        return <DetailSkeleton sx={{ p: { xs: 2, md: 3 } }} />;
    }
    return (
        // 100dvh, not 100vh: on a phone 100vh is the height with the browser's
        // toolbars HIDDEN, taller than the screen while they show. The frame then
        // overflowed html/body (height 100%), the document gained a hidden scroll
        // range, and the pane's last rows sat under Safari's bottom bar.
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', '@supports (height: 100dvh)': { height: '100dvh' } }}>
            {!ownDomainFlow(shopifyConnection) && (
                <ShopifyConnectPrompt key={shopifyPromptKey} onNotify={(message, severity) => setShopifyNote({ message, severity })} />
            )}
            <Snackbar
                open={!!shopifyNote} autoHideDuration={6000} onClose={() => setShopifyNote(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity={shopifyNote?.severity || 'info'} onClose={() => setShopifyNote(null)}>
                    {shopifyNote?.message}
                </Alert>
            </Snackbar>
            {/* App Bar */}
            {/* The bar reads as a MATERIAL rather than a banner. Three quiet layers do it:
                a soft brand glow behind the logo, a shallow vertical gradient (light at the
                top, as if lit from above), and a 1px inner highlight along the very top edge.
                The old 90° three-stop gradient swept left-to-right, which made the bar the
                loudest thing on screen; a vertical fall-off just gives it depth. It closes
                with a hairline and a soft shadow, so the page appears to sit UNDER the bar
                instead of being fenced off by a bright border. */}
            <AppBar position="static" elevation={0} sx={{
                pt: 0.75, pb: 0.75,
                backgroundImage: 'radial-gradient(680px 150px at 8% -30%, rgba(96,165,250,0.30), transparent 70%),'
                    + 'linear-gradient(180deg, #24468f 0%, #1b3477 100%)',
                backgroundColor: '#1e3a8a',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.07), inset 0 -1px 0 rgba(255,255,255,0.10),'
                    + '0 8px 22px -16px rgba(15,23,42,0.55)',
            }}>
                <Toolbar sx={{ minHeight: { xs: 56, sm: 60 } }}>
                    {isMobile && (
                        <Tooltip title="Open menu" arrow enterDelay={0}>
                            <IconButton color="inherit" onClick={() => setOpenDrawer(true)} edge="start" sx={{ mr: 0.5 }}>
                                <MenuIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                    {!isMobile && (
                        <Box component={Link} to="/dashboard/domains"
                            sx={{
                                display: 'inline-flex', alignItems: 'center', textDecoration: 'none',
                                borderRadius: 2, px: 0.5, py: 0.5, transition: 'opacity .16s ease',
                                opacity: 0.96, '&:hover': { opacity: 1 },
                                '&:focus-visible': { outline: '2px solid rgba(255,255,255,0.75)', outlineOffset: 2 },
                            }}>
                            <Box component="img" src={logoWhite} alt="XEO" sx={{ width: 60, height: 'auto' }} />
                        </Box>
                    )}
                    <Typography paddingLeft={isMobile ? "0" : "25px"} variant="h6" sx={{ flexGrow: 1 }}>

                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "12px", mr: 0, pr: 0 }}>
                        {/* Two states. The icon shows what a click will GIVE you, which is
                            the convention people already know from every OS toggle. */}
                        <Tooltip arrow title={colorMode.resolved === 'dark' ? 'Switch to light' : 'Switch to dark'}>
                            <IconButton
                                onClick={colorMode.toggle}
                                aria-label={colorMode.resolved === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
                                sx={{
                                    width: 36, height: 36, color: '#fff',
                                    backgroundColor: 'rgba(255,255,255,0.10)',
                                    border: '1px solid rgba(255,255,255,0.18)',
                                    backdropFilter: 'blur(6px)',
                                    transition: 'background-color .18s ease, border-color .18s ease',
                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.16)', borderColor: 'rgba(255,255,255,0.3)' },
                                }}>
                                {colorMode.resolved === 'dark'
                                    ? <LightModeRoundedIcon sx={{ fontSize: 19 }} />
                                    : <DarkModeRoundedIcon sx={{ fontSize: 19 }} />}
                            </IconButton>
                        </Tooltip>

                        {/* Profile Button */}
                        {/* Glass, not a solid white lozenge. A white pill on a blue bar is the
                            brightest object on the screen and pulls the eye away from the page;
                            translucent white with a hairline sits ON the bar and still reads as
                            a control. The credit figure keeps its warm accent — the one place a
                            second hue is earned, because it's a number people watch. */}
                        <Box
                            onClick={toggleDropdown}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleDropdown(e); } }}
                            aria-label={creditsError ? 'Account and credits (balance unavailable)' : 'Account and credits'}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "9px",
                                padding: isSmallScreen ? "6px 8px" : "5px 13px",
                                borderRadius: 999,
                                backgroundColor: "rgba(255,255,255,0.10)",
                                border: "1px solid rgba(255,255,255,0.18)",
                                color: "#fff",
                                cursor: "pointer",
                                backdropFilter: "blur(6px)",
                                transition: "background-color .18s ease, border-color .18s ease, transform .18s ease",
                                "&:hover": {
                                    backgroundColor: "rgba(255,255,255,0.16)",
                                    borderColor: "rgba(255,255,255,0.3)",
                                    transform: "translateY(-1px)",
                                },
                                "&:focus-visible": { outline: "2px solid rgba(255,255,255,0.8)", outlineOffset: 2 },
                            }}
                        >
                            <AccountCircleIcon sx={{ fontSize: 22, opacity: 0.92 }} />
                            {/* Phones get the balance alone (no divider, no "Credits" word). The
                                full readout below is hidden under sm and the account menu has no
                                balance line, so without this the number shows nowhere on a phone. */}
                            {isSmallScreen && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <LocalFireDepartmentIcon sx={{ fontSize: 17, color: '#fcd34d' }} />
                                    <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#fcd34d', fontVariantNumeric: 'tabular-nums' }}>
                                        {!planLoaded
                                            ? <Skeleton variant="text" width={22} sx={{ display: 'inline-block' }} />
                                            : creditsError ? '—' : (Math.round((Number(planData.remaining_credit) || 0) * 100) / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}
                                    </Typography>
                                </Box>
                            )}
                            {!isSmallScreen && (
                                <>
                                    <Divider
                                        orientation="vertical"
                                        flexItem
                                        sx={{
                                            height: 20, alignSelf: 'center',
                                            borderRightWidth: 1,
                                            borderColor: 'rgba(255,255,255,0.24)',
                                        }}
                                    />

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <LocalFireDepartmentIcon sx={{ fontSize: 17, color: '#fcd34d' }} />
                                        <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#fcd34d', fontVariantNumeric: 'tabular-nums' }}>
                                            {/* Until the credit fetch finishes, show a skeleton — `loading`
                                                flips to false on auth-resolve, before the balance arrives,
                                                so without this the chip flashes a premature 0 on hard refresh.
                                                A read that FAILED is "—", not 0 (retry in the menu). */}
                                            {!planLoaded
                                                ? <Skeleton variant="text" width={22} sx={{ display: 'inline-block' }} />
                                                : creditsError ? '—' : (Math.round((Number(planData.remaining_credit) || 0) * 100) / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}
                                        </Typography>

                                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>
                                            Credits
                                        </Typography>

                                    </Box>
                                </>
                            )}
                        </Box>
                    </Box>
                </Toolbar>
            </AppBar>

            <Menu
                anchorEl={anchorEl}
                open={dropdownOpen}
                onClose={handleClose}
                autoFocus={false}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        mt: 1,
                        minWidth: 180,
                        maxWidth: 400,
                        boxShadow: "0px 6px 14px rgba(0,0,0,0.15)",
                        "& .MuiMenuItem-root": {
                            fontSize: 14,
                            borderRadius: 1,
                            "&:hover": {
                                backgroundColor: "action.hover",
                            },
                        },
                    },
                }}

            >
                <Typography sx={{ px: 3, pt: 1.5, pb: 0.5, fontSize: 12.5, color: 'text.secondary', lineHeight: 1.45 }}>
                    Signed in as
                    <Box component="span" sx={{ display: 'block', fontWeight: 700, color: 'var(--ink)', fontSize: 14, wordBreak: 'break-all' }}>
                        {user?.email}
                    </Box>
                </Typography>
                {creditsError && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, py: 0.75 }}>
                        <Typography sx={{ flex: 1, fontSize: 12.5, color: 'var(--warn-fg)', lineHeight: 1.45 }}>
                            Couldn’t load your credit balance.
                        </Typography>
                        <Button size="small" onClick={retryBalance} disabled={retryingBalance}
                            sx={{ textTransform: 'none', fontWeight: 700, fontSize: 13, minWidth: 0 }}>
                            {retryingBalance ? 'Retrying…' : 'Retry'}
                        </Button>
                    </Box>
                )}
                {/* Plan name + Upgrade only when the plans experience is on.
                    Credits-only mode drops both (the credit balance stays in the
                    top bar). */}
                {plansEnabled && (
                    <Typography sx={{ display: "flex", px: 2, py: 1, color: "text.secondary" }}>
                        <Typography
                            component="span"
                            fontWeight={500}
                            sx={{
                                ml: 1,
                                px: 1,
                                py: 0.1,
                                bgcolor: 'var(--good-bg)',
                                borderRadius: 1,
                                fontSize: 14
                            }}
                        >
                            {planData?.planName || "Free"}  Plan
                        </Typography>
                        <Button
                            onClick={handleClose}
                            component={Link}
                            to={
                                shopifyConnection &&
                                    shopifyConnection.source !== 'add' &&
                                    shopifyConnection.source !== 'edit'
                                    ? '/dashboard/connector/plans'
                                    : '/dashboard/plans'
                            }
                            sx={{ px: 2, pt: 1, lineHeight: 0.5, color: "primary.main", fontSize: 14, textTransform: "none" }}
                        >
                            Upgrade
                        </Button>
                    </Typography>
                )}

                {/* Credits-only account card: balance + a tidy Buy Credits CTA,
                    in a soft padded card (replaces the plan name + Upgrade row). */}
                {!plansEnabled && (
                    <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
                        <Button
                            onClick={handleClose}
                            component={Link}
                            to={
                                shopifyConnection &&
                                    shopifyConnection.source !== 'add' &&
                                    shopifyConnection.source !== 'edit'
                                    ? '/dashboard/connector/plans'
                                    : '/dashboard/plans'
                            }
                            fullWidth
                            variant="contained"
                            disableElevation
                            sx={{ borderRadius: 1.75, textTransform: 'none', fontWeight: 700, fontSize: 14, py: 0.9 }}
                        >
                            Buy Credits
                        </Button>
                    </Box>
                )}

                <Divider sx={{ my: 1, mx: 1 }} />

                <MenuItem
                    onClick={handleClose}
                    component={Link}
                    to="/dashboard/profile"
                    sx={{
                        px: 3,
                        py: 1.25,
                        color: "primary.main",
                    }}
                >
                    <PersonOutlineIcon sx={{ fontSize: 20, mr: 2 }} />
                    <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                        Profile
                    </Typography>
                </MenuItem>

                {/* ONE Google entry. "Google Accounts" and "Google Integration" were two
                    separate menu items pointing at two pages that overlapped heavily; they
                    are now a single destination. */}
                <MenuItem
                    onClick={handleClose}
                    component={Link}
                    to={GOOGLE_UNIFIED_PATH}
                    sx={{
                        px: 3,
                        py: 1.25,
                        color: "primary.main",
                    }}
                >
                    <ManageAccountsOutlinedIcon sx={{ fontSize: 20, mr: 2 }} />
                    <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                        Google Integration
                    </Typography>
                </MenuItem>

                <Divider sx={{ mx: 1 }} />

                <MenuItem
                    onClick={handleLogout}
                    component="a"
                    sx={{
                        px: 3,
                        py: 1.25,
                        color: "var(--ink-2)", // sign-out isn't a nav link: neutral, not primary blue
                    }} >
                    <LogoutIcon sx={{ fontSize: 20, mr: 2 }} />
                    <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                        Logout
                    </Typography>
                </MenuItem>
            </Menu>

            {/* Body */}
            <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
                {/* Drawer - Now used for mobile, landscape, and tablets (below md breakpoint) */}
                {isMobile && (
                    <Drawer
                        anchor="left"
                        open={openDrawer}
                        onClose={() => setOpenDrawer(false)}
                        PaperProps={{
                            // Same rail treatment as the desktop sidebar, so the nav
                            // doesn't look like a different surface on a phone.
                            'data-xeo-rail': '',
                            sx: {
                                width: { xs: 250, sm: 280 }, // Slightly wider on larger mobile devices
                                // Dark mode: Paper's elevation overlay (a white linear-gradient)
                                // replaced the rail's glow and washed the drawer grey. Paint the
                                // desktop rail's ground explicitly; the shadow stays (audit 2026-11-01).
                                backgroundColor: 'var(--surface)',
                                backgroundImage: 'radial-gradient(320px 260px at 0% 0%, var(--art-glow-rail), transparent 74%)',
                                backgroundRepeat: 'no-repeat',
                            }
                        }}
                    >
                        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none',}}>
                                <List sx={{ mt: 1, width: '100%' }}>
                                    {mainMenuSections.map((section, sectionIndex) => (
                                        <React.Fragment key={sectionIndex}>
                                            {/* Section Title */}
                                            {/* Same caption as the desktop rail (uppercase, spaced) so
                                                the phone nav doesn't read as a different menu (audit 2026-11-01). */}
                                            <ListItem disablePadding sx={{ px: 3, pt: sectionIndex !== 0 ? 2 : 0 }}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontSize: '0.7rem',
                                                        fontWeight: 700,
                                                        letterSpacing: '0.07em',
                                                        textTransform: 'uppercase',
                                                        color: 'var(--ink-3)',
                                                        py: 1,
                                                    }}
                                                >
                                                    {section.title}
                                                </Typography>
                                            </ListItem>

                                            {/* Section Items */}
                                            {section.items.map((item) => (
                                                <React.Fragment key={item.key}>
                                                <ListItem disablePadding sx={{ px: 1 }}>
                                                    <ListItemButton
                                                        component={item.key === 'xsite-builder' ? 'button' : Link}
                                                        to={item.key === 'xsite-builder' ? undefined : (item.path || `/dashboard/${item.key}`)}
                                                        selected={selectedItem === item.key}
                                                        sx={{
                                                            textDecoration: 'none', color: 'inherit', borderRadius: '10px', mb: 0.25,
                                                            py: 0.6, minHeight: 40, // desktop rail's 40px rows
                                                            position: 'relative', overflow: 'hidden',
                                                            transition: 'background-color .18s ease',
                                                            '&:hover': { backgroundColor: 'var(--tint)' },
                                                            '&.Mui-selected': {
                                                                backgroundColor: 'var(--tint-2)',
                                                                '&:hover': { backgroundColor: 'var(--tint-2)' },
                                                                '& .MuiTypography-root': { color: 'var(--accent)', fontWeight: 700 },
                                                                '& .MuiSvgIcon-root': { color: 'var(--accent)' },
                                                                // The marker: unmistakable at a glance, unlike a tint alone.
                                                                '&::before': {
                                                                    content: '""', position: 'absolute', left: 0, top: '50%',
                                                                    transform: 'translateY(-50%)',
                                                                    width: 3, height: 20, borderRadius: '0 3px 3px 0',
                                                                    backgroundColor: '#1976d2',
                                                                },
                                                            },
                                                        }}
                                                        onClick={(e) => {
                                                            if (e.ctrlKey || e.metaKey || e.button === 1) return;
                                                            e.preventDefault();
                                                            handleItemClick(item.key, item.path);
                                                        }}
                                                    >
                                                        <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                                                        <ListItemText sx={{
                                                            my: 0,
                                                            '& .MuiTypography-root': {
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                color: 'var(--ink-2)',
                                                                fontWeight: 500
                                                            }
                                                        }} primary={item.label} />
                                                        {item.children?.length ? (
                                                            <Box component="span" onClick={(e) => toggleMenu(item.key, e)}
                                                                sx={{ display: 'inline-flex', ml: 'auto', p: 0.25, borderRadius: '6px', '&:hover': { backgroundColor: 'var(--wash)' }, position: 'relative', '&::after': { content: '""', position: 'absolute', inset: -5 } }}>
                                                                <KeyboardArrowDownRounded sx={{ fontSize: 18, color: 'var(--ink-4)', transition: 'transform .15s', transform: openMenus[item.key] ? 'rotate(180deg)' : 'none' }} />
                                                            </Box>
                                                        ) : item.path ? (
                                                            <Tooltip title="Opens in SEO Suite" arrow placement="right">
                                                                <NorthEastRounded sx={{ fontSize: 15, color: 'var(--ink-4)', ml: 'auto' }} />
                                                            </Tooltip>
                                                        ) : null}
                                                    </ListItemButton>
                                                </ListItem>
                                                {renderSubmenu(item)}
                                                </React.Fragment>
                                            ))}

                                            {/* Divider between sections */}
                                            {sectionIndex < mainMenuSections.length - 1 && (
                                                <Divider
                                                    sx={{
                                                        my: 2,
                                                        mt: 3,
                                                        // No border: MUI's 1px (transparent) bottom border filled the 1px
                                                        // height, leaving a 0px padding box to size the gradient in, so
                                                        // the rule never painted.
                                                        border: 0,
                                                        height: '1px',
                                                        background: (theme) =>
                                                            `linear-gradient(to right, transparent, ${theme.palette.divider}, transparent)`,
                                                    }}
                                                />
                                            )}
                                        </React.Fragment>
                                    ))}
                                </List>
                            </Box>
                        </Box>
                    </Drawer>
                )}

                {/* Desktop Sidebar - Only shown on screens md and above */}
                {!isMobile && (
                    /* data-xeo-rail: the nav gets its own, quieter cut of the page
                       backdrop (see index.css). Without it the rail read as a flat
                       white slab against the textured pane beside it. */
                    <Box data-xeo-rail="" sx={{
                        width: collapsed ? 80 : 240,
                        backgroundColor: 'var(--surface)',
                        borderRight: '1px solid var(--line)',
                        transition: 'width 0.3s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        height: '100%',
                        '&::after': isGlobalLoading ? {} : {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            width: '1px',
                            height: '100%',
                            zIndex: 1,
                        }
                    }}>
                        {/* Main Menu - Now with proper scrolling */}
                        <Box ref={setRailScroller} sx={{
                            flexGrow: 1,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            // Same reason as the content pane: the sidebar must not drag the
                            // whole frame when its list ends.
                            overscrollBehavior: 'contain',
                            // Invisible scrollbar — scrolls, but no visible track/thumb.
                        }}>
                            {/* Collapsed: no bottom padding. The toggle row below is the gap,
                                and those 8px were all that made the rail scroll at 900px tall.
                                The open rail scrolls there anyway and keeps them. */}
                            <List sx={{ mt: 1, width: '100%', pb: collapsed ? 0 : 1 }}>
                                {mainMenuSections.map((section, sectionIndex) => (
                                    <React.Fragment key={sectionIndex}>
                                        {/*
                                          * Title — OPEN RAIL ONLY.
                                          *
                                          * The collapsed rail carries no group labels: the
                                          * dividers separate the groups, and each icon already
                                          * names itself on hover. There is no abbreviation of
                                          * "XWatch" that is both readable at 80px and worth the
                                          * row it costs — the attempt wrapped to "WATC / H", and
                                          * shortening further only trades one unreadable thing
                                          * for another.
                                          */}
                                        {!collapsed && (
                                            <ListItem data-nav-caption="" disablePadding sx={{ px: 2.5, width: '100%' }}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontSize: '0.7rem',
                                                        fontWeight: 700,
                                                        letterSpacing: '0.07em',
                                                        textTransform: 'uppercase',
                                                        color: 'var(--ink-3)', // small text: --ink-3 (4.76:1), never --ink-4
                                                        py: 0.25,
                                                    }}
                                                >
                                                    {section.title}
                                                </Typography>
                                            </ListItem>
                                        )}

                                        {/* Items */}
                                        {section.items.map((item) => (
                                            <React.Fragment key={item.key}>
                                            <ListItem data-nav-key={item.key} disablePadding sx={{
                                                px: 1.2,
                                                width: '100%',
                                                display: 'block',
                                            }}>
                                                {collapsed ? (
                                                    <Tooltip title={item.path ? `${item.label} — opens in SEO Suite` : item.label} arrow placement="right">
                                                        <ListItemButton
                                                            component={Link}
                                                            to={item.path || `/dashboard/${item.key}`}
                                                            selected={selectedItem === item.key}
                                                            sx={{
                                                                py: 1,
                                                                flexDirection: 'column',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                borderRadius: '50px',
                                                                width: '100%',
                                                                maxWidth: '60px',
                                                                minWidth: '60px',
                                                                '&:hover': {
                                                                    backgroundColor: 'action.hover',
                                                                },
                                                            }}
                                                            onClick={(e) => {
                                                                if (e.ctrlKey || e.metaKey || e.button === 1) return;
                                                                e.preventDefault();
                                                                handleItemClick(item.key, item.path);
                                                            }}
                                                        >
                                                            <ListItemIcon sx={{ minWidth: 'auto', justifyContent: 'center', px: 1.2 }}>
                                                                {item.icon}
                                                            </ListItemIcon>
                                                        </ListItemButton>
                                                    </Tooltip>
                                                ) : (
                                                    <ListItemButton
                                                        component={Link}
                                                        to={item.path || `/dashboard/${item.key}`}
                                                        selected={selectedItem === item.key}
                                                        sx={{
                                                            py: 0.6,
                                                            px: 1.1,
                                                            mb: 0.25,
                                                            minHeight: 40,
                                                            borderRadius: '10px',
                                                            flexDirection: 'row',
                                                            alignItems: 'center',
                                                            justifyContent: 'flex-start',
                                                            width: '100%',
                                                            maxWidth: '220px',
                                                            minWidth: '220px',
                                                            '&:hover': {
                                                                backgroundColor: 'var(--tint)',
                                                            },
                                                            // Distinct selected state: tinted pill + accent + bold blue.
                                                            '&.Mui-selected': {
                                                                backgroundColor: 'var(--tint-2)',
                                                                '&:hover': { backgroundColor: 'var(--tint-2)' },
                                                                '& .MuiTypography-root': { color: 'var(--accent)', fontWeight: 700 },
                                                                '& .MuiSvgIcon-root': { color: 'var(--accent)' },
                                                            },
                                                            // One-shot pulse highlight when arriving back from
                                                            // a domain-registered event. 3 cycles × 2s = 6s, then
                                                            // the state flips off and the animation stops.
                                                            ...(highlightDomains && item.key === 'domains' && {
                                                                '@keyframes xeoDomainsPulse': {
                                                                    '0%, 100%': { boxShadow: '0 0 0 0 rgba(25,118,210,0)' },
                                                                    '50%': { boxShadow: '0 0 0 6px rgba(25,118,210,0.18)' },
                                                                },
                                                                animation: 'xeoDomainsPulse 2s ease-in-out 3',
                                                                bgcolor: 'rgba(25,118,210,0.06)',
                                                                borderRadius: '8px',
                                                            }),
                                                        }}
                                                        onClick={(e) => {
                                                            if (e.ctrlKey || e.metaKey || e.button === 1) return;
                                                            e.preventDefault();
                                                            handleItemClick(item.key, item.path);
                                                        }}
                                                    >
                                                        <ListItemIcon sx={{ minWidth: 'auto', justifyContent: 'center' }}>
                                                            {item.icon}
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            sx={{
                                                                pl: 1.25,
                                                                my: 0,
                                                                '& .MuiTypography-root': {
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    color: 'var(--ink-2)',
                                                                    fontWeight: 500,
                                                                    fontSize: '0.9rem',
                                                                }
                                                            }}
                                                            primary={item.label}
                                                        />
                                                        {item.children?.length ? (
                                                            <Box component="span" onClick={(e) => toggleMenu(item.key, e)}
                                                                sx={{ display: 'inline-flex', ml: 'auto', p: 0.25, borderRadius: '6px', '&:hover': { backgroundColor: 'var(--wash)' }, position: 'relative', '&::after': { content: '""', position: 'absolute', inset: -5 } }}>
                                                                <KeyboardArrowDownRounded sx={{ fontSize: 18, color: 'var(--ink-4)', transition: 'transform .15s', transform: openMenus[item.key] ? 'rotate(180deg)' : 'none' }} />
                                                            </Box>
                                                        ) : item.path ? (
                                                            <Tooltip title="Opens in SEO Suite" arrow placement="right">
                                                                <NorthEastRounded sx={{ fontSize: 15, color: 'var(--ink-4)', ml: 'auto' }} />
                                                            </Tooltip>
                                                        ) : null}
                                                    </ListItemButton>
                                                )}
                                            </ListItem>
                                            {!collapsed && renderSubmenu(item)}
                                            </React.Fragment>
                                        ))}

                                        {/* Divider between sections */}
                                        {sectionIndex < mainMenuSections.length - 1 && (
                                            <Divider
                                                sx={{
                                                    my: collapsed ? 1 : 1.25,
                                                    borderColor: 'transparent',
                                                    height: '1px',
                                                    width: '100%',
                                                    /*
                                                     * A GRADIENT fading to transparent at both
                                                     * ends needs width to be visible. The rail
                                                     * is 240px open and 80px collapsed, and at
                                                     * 80 the fades meet in the middle: the line
                                                     * between two groups all but disappeared,
                                                     * which is how XWatch and XWrite came to
                                                     * read as one block of eight icons.
                                                     *
                                                     * Collapsed gets a plain inset rule instead —
                                                     * the same colour, actually drawn.
                                                     */
                                                    ...(collapsed
                                                        ? {
                                                            mx: 'auto',
                                                            width: '60%',
                                                            backgroundColor: (theme) => theme.palette.divider,
                                                        }
                                                        : {
                                                            background: (theme) =>
                                                                `linear-gradient(to right, transparent, ${theme.palette.divider}, transparent)`,
                                                        }),
                                                }}
                                            />
                                        )}
                                    </React.Fragment>
                                ))}
                            </List>
                        </Box>

                        {/*
                          * The collapse toggle gets a row of its own at the foot of the
                          * rail. It used to float over the scrolling list (absolute,
                          * bottom 30), so at 900px tall the collapsed rail's chevron sat
                          * on its last nav item, and on shorter screens over whichever
                          * items scrolled beneath it. Outside the list's box, the list
                          * ends where the toggle begins at every height. 64px is an 8px
                          * gap, the 40px button and 16px to the edge — little enough that
                          * the collapsed rail still fits a 900px screen without scrolling.
                          */}
                        <Box sx={{
                            position: 'relative', flexShrink: 0, height: 64,
                            // The rows fade into the rail's own ground along the row's top edge
                            // while the list runs on beneath it: scrollbars are hidden app-wide,
                            // so this is the only sign there's more. A fade, not a shadow — a
                            // darkening is invisible on the dark rail (1.04:1).
                            '&::before': {
                                content: '""', position: 'absolute', left: 0, right: 0, top: -16, height: 16,
                                pointerEvents: 'none',
                                background: 'linear-gradient(to top, var(--surface), transparent)',
                                opacity: railMoreBelow ? 1 : 0,
                                transition: 'opacity .2s ease',
                            },
                        }}>
                            <Box sx={{
                                position: 'absolute',
                                bottom: 16,
                                left: collapsed ? 20 : 220,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 2,
                                width: 40,
                                height: 40,
                                transition: 'left 0.3s ease',
                            }}>
                                <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right" arrow enterDelay={0}>
                                    <IconButton
                                        onClick={handleCollapse}
                                        disabled={isTransitioning}
                                        sx={{
                                            backgroundColor: isGlobalLoading ? 'transparent' : 'var(--surface-2)',
                                            transition: 'border 0.3s ease',
                                            boxShadow: '0px 0px 5px rgba(0,0,0,0.1)',
                                            '&:hover': {
                                                backgroundColor: 'var(--line)',
                                            }
                                        }}
                                    >
                                        {(isTransitioning ? collapsed : collapsed) ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    </Box>
                )}

                {/* Main Content */}
                {/* overscrollBehavior: reaching the top or bottom of this pane used to
                    chain to the document and rubber-band the entire shell, so the top bar
                    lifted and bare page ground flashed above it. The scroll now stops here.
                    Ground colour is the theme's, not a warmer stray grey.
                    This pane is what scrolls, so window.scrollTo does nothing here —
                    use utils/scrollPane's scrollToPageTop. */}
                <Box sx={{
                    // Transparent, not the flat ground: the page backdrop is painted
                    // on <body> (see index.css) and an opaque pane here would cover
                    // it. Cards keep their own opaque --surface, so the backdrop only
                    // ever shows in the gutters between them.
                    flexGrow: 1, backgroundColor: 'transparent',
                    padding: { xs: 2, sm: 3 },
                    // 'auto', not 'scroll': a short page should not be scrollable at all.
                    overflowY: 'auto', overflowX: 'auto', overscrollBehavior: 'contain',
                    // This is the pane the whole app scrolls in, so its scrollbar
                    // appearing is a LAYOUT change, not a paint: the content box
                    // narrows and every page re-flows sideways the moment a fetch
                    // makes the page taller than the viewport. Hiding it is what fixes
                    // that — a zero-width scrollbar can't take space away from the
                    // content, so there is nothing to shift. Reserving a gutter also
                    // works, but it spends real width on an empty strip forever to
                    // avoid a few px of movement once.
                }}>
                    {/* Soft Fade keyed by pathname so dashboard route changes feel gentle */}
                    <Fade key={location.pathname} in timeout={260} appear>
                        <Box>
                            <Outlet context={{ connectionId: shopifyConnection?.value ?? null, shop: shopifyConnection?.shop ?? null, source: shopifyConnection?.source ?? null, userId: shopifyConnection?.userId ?? null }} />
                        </Box>
                    </Fade>
                </Box>
            </Box>
        </Box >
    );
};

export default Dashboard;